import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect } from "react";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import instrumentSerifWoff2 from "@fontsource/instrument-serif/files/instrument-serif-latin-400-normal.woff2?url";
import workSansWoff2 from "@fontsource-variable/work-sans/files/work-sans-latin-wght-normal.woff2?url";
import { SiteLayout } from "../components/site/SiteLayout";
import track from "@/lib/analytics/track";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { registerClientErrorReporter } from "@/lib/client-error-reporter";

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="text-amber-200 text-xs font-semibold uppercase tracking-widest mb-4">404</p>
        <h1 className="font-serif text-5xl text-white">Page not found</h1>
        <p className="mt-4 text-sm text-zinc-400">The page you're looking for doesn't exist or has been moved.</p>
        <Link to="/" className="mt-8 inline-flex bg-white text-zinc-950 px-6 py-3 rounded-full text-sm font-semibold hover:bg-amber-300 hover:text-white transition-colors">Return home</Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Michael's Painting — See Your Home Before You Paint It" },
      { name: "description", content: "Upload a photo, preview real Sherwin-Williams colors with our AI Paint Studio, and get an instant painting estimate. Wisconsin's modern painting contractor." },
      { name: "author", content: "Michael's Painting" },
      { property: "og:site_name", content: "Michael's Painting" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:title", content: "Michael's Painting — See Your Home Before You Paint It" },
      { name: "twitter:title", content: "Michael's Painting — See Your Home Before You Paint It" },
      { property: "og:description", content: "Upload a photo, preview real Sherwin-Williams colors with our AI Paint Studio, and get an instant painting estimate. Wisconsin's modern painting contractor." },
      { name: "twitter:description", content: "Upload a photo, preview real Sherwin-Williams colors with our AI Paint Studio, and get an instant painting estimate. Wisconsin's modern painting contractor." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/a291eaa5-c60c-41c6-bde3-f1725a739171" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/a291eaa5-c60c-41c6-bde3-f1725a739171" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: instrumentSerifWoff2,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: workSansWoff2,
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "PaintingService",
          name: "Michael's Painting",
          url: "https://michaelspaintingwi.com",
          image: "https://michaelspaintingwi.com/og-cover.jpg",
          areaServed: [
            "Madison, WI", "Middleton, WI", "Fitchburg, WI", "Verona, WI",
            "Sun Prairie, WI", "Waunakee, WI", "McFarland, WI", "Stoughton, WI",
          ],
          address: {
            "@type": "PostalAddress",
            addressLocality: "Madison",
            addressRegion: "WI",
            addressCountry: "US",
          },
          priceRange: "$$$",
          telephone: "+1-608-886-4598",
          email: "michaelspainting0@gmail.com",
          sameAs: ["https://www.facebook.com/"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://michaelspaintingwi.com/#business",
          name: "Michael's Painting",
          url: "https://michaelspaintingwi.com",
          image: "https://michaelspaintingwi.com/og-cover.jpg",
          address: {
            "@type": "PostalAddress",
            streetAddress: "4012 Terminal Dr",
            addressLocality: "McFarland",
            addressRegion: "WI",
            postalCode: "53558",
            addressCountry: "US",
          },
          areaServed: "Madison Metro, Wisconsin",
          telephone: "+1-608-886-4598",
          email: "michaelspainting0@gmail.com",
          priceRange: "$$$",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    registerClientErrorReporter();
  }, []);

  useEffect(() => {
    // skip admin-internal pages from analytics
    if (pathname.startsWith("/admin") || pathname.startsWith("/setup-admin") || pathname.startsWith("/auth")) return;
    track.pageview(pathname);
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SiteLayout>
          <Outlet />
        </SiteLayout>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
